"use client";

import { WorkspaceSurface } from '@unitfield/ui/components/ui/workspace-panel';
import { WorkspaceActionOutlet, WorkspaceActions, WorkspaceDraft, useWorkspaceActionsStore, useWorkspaceEditHistory } from '@unitfield/ui/components/ui/workspace-actions';
import { useEffect, useSyncExternalStore, useId, useMemo, useRef, useState, type ChangeEvent, type DragEvent } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useRouter } from 'next/navigation';
import type { ApplicationAttachment, ApplicationDraft, ApplicationQuestion, SubmitApplicationResult } from '@unitfield/sdk';
import { UnitfieldIcon } from '@unitfield/ui/icons';
import { customToast } from '@unitfield/ui/components/ui/ark-toast';
import { cn } from '@unitfield/ui/lib/utils';
import { PublicRouteLink, usePublicRouteBridge } from '@unitfield/ui/public-rental/public-route-bridge';
import { isRentalApplicationStepCompleted } from '@unitfield/ui/public-rental/lib/application-progress';
import { rentalListingPath, type PublicRentalApplicationUnitLocationState } from '@unitfield/ui/public-rental/public-route-contracts';
import { PublicLoading, usePublicShell } from '@unitfield/ui/public-rental/shell';
import shell from '@unitfield/ui/public-rental/shell/shell.module.css';
import { describeMoveInFit, formatShortDate, formatRent, parseIsoDate, startOfDay, toIsoDate, unitTitle } from '@unitfield/ui/public-rental/rental-applications/listing-model';
import { applicationCardLines, screeningNotice, type ScreeningFacts } from '@unitfield/ui/public-rental/rental-applications/apply/application-card-model';
import { ApplicationCard, ApplyFrame, HomeCard, PhotoPanel } from '@unitfield/ui/public-rental/rental-applications/apply/apply-frame';
import { StartScreen, TaskScreen } from '@unitfield/ui/public-rental/rental-applications/apply/apply-screens';
import { AboutYouStep, ApplyEmpty, CustomQuestion, DocumentsStep, EmploymentStep, MoveInStep, ResidencesStep, SignStep, type FieldA11y } from '@unitfield/ui/public-rental/rental-applications/apply/apply-steps';
import applyStyles from '@unitfield/ui/public-rental/rental-applications/apply/apply.module.css';
import { applicantDocumentIds, type ApplicantLegalProjection } from '@unitfield/ui/public-rental/lib/legal';
import { applyApi, ApplyRequestError } from './apply-api';
import { employmentPayload, needsEmailRepair, withVerifiedEmail, type EmploymentEntry } from './payload-rules';

type PublicDraftAttachmentResponse = Omit<ApplicationAttachment, 'fileId'>;

/** The browser never needs the storage file id. */
function withoutFileId(attachment: ApplicationAttachment): PublicDraftAttachmentResponse {
  const { fileId, ...rest } = attachment;
  void fileId;
  return rest;
}

type SubmittedApplicationReceipt = SubmitApplicationResult;

const GUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function isSubmittedApplicationReceipt(value: unknown): value is SubmittedApplicationReceipt {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return false;
  const receipt = value as Record<string, unknown>;
  return typeof receipt.applicationId === 'string' && GUID.test(receipt.applicationId) && receipt.applicationId !== '00000000-0000-0000-0000-000000000000';
}

type StepKey =
  | 'move_in'
  | 'household'
  | 'about_you'
  | 'employment'
  | 'residential_history'
  | 'custom_questions'
  | 'attachments'
  | 'review';

const allSteps: StepKey[] = [
  'move_in',
  'household',
  'about_you',
  'employment',
  'residential_history',
  'custom_questions',
  'attachments',
  'review',
];

export interface PublicRentalApplicationApplyCoreProps {
  /** The host the application is for. It scopes the editing history and keys. */
  organizationSlug: string;
  /** The key in `/apply/{unitPublicKey}`. */
  unitPublicKey: string;
}

const subscribeHydration = () => () => {};
/** One question per screen, in plain words (spec §5.3). `household` shares the move-in screen. */
const stepQuestions: Record<StepKey, string> = {
  move_in: 'When do you want to move in?',
  household: 'When do you want to move in?',
  about_you: 'About you',
  employment: 'Where do you work?',
  residential_history: 'Where have you lived?',
  custom_questions: 'A few more questions',
  attachments: 'Documents',
  review: 'Sign and send',
};

/** Short names for the back button's accessible label. */
const stepShortNames: Record<StepKey, string> = {
  move_in: 'Move-in',
  household: 'Move-in',
  about_you: 'About you',
  employment: 'Work',
  residential_history: 'Homes',
  custom_questions: 'Questions',
  attachments: 'Documents',
  review: 'Sign and send',
};

const stepLeads: Partial<Record<StepKey, string>> = {
  about_you: 'How the business can reach you.',
  employment: 'Add each job one at a time. Include any income you want counted.',
  residential_history: 'Add the homes you’ve lived in recently, one at a time. This is optional.',
  attachments: 'Pay stubs, an offer letter or anything else that helps. This is optional.',
  review: 'Check your application on the right. Tap any line to change it.',
};

type TemplateQuestion = Omit<ApplicationQuestion, 'type' | 'orderIndex'> & {
  type: string;
  orderIndex: number;
};

type CustomAnswerValue = string | number | boolean | string[] | null;
type FieldErrors = Record<string, string>;
type TouchedFieldMap = Record<string, boolean>;
type SubmittedStepMap = Partial<Record<StepKey, boolean>>;
type StepCompletionMap = Partial<Record<StepKey, boolean>>;


interface ResidenceEntry {
  occupancy_type: string;
  address_line_1: string;
  city: string;
  state: string;
  move_in_date: string;
  move_out_date: string;
}

interface StepValidationContext {
  employmentEntries: EmploymentEntry[];
  residenceEntries: ResidenceEntry[];
  templateQuestions: TemplateQuestion[];
}

const employmentJobTypes = ['Employed', 'SelfEmployed', 'Unemployed', 'Student', 'Other'] as const;
const occupancyTypes = ['Rent', 'Own', 'Other'] as const;
const attachmentTypes = ['EmploymentLetter', 'EnrollmentLetter', 'Other'] as const;
const attachmentAccept = '.pdf,.docx,.jpg,.jpeg,.png,.webp';
const attachmentExtensions = new Set(['pdf', 'docx', 'jpg', 'jpeg', 'png', 'webp']);
const maxAttachmentSizeBytes = 10 * 1024 * 1024;

const attachmentTypeLabels: Record<(typeof attachmentTypes)[number], string> = {
  EmploymentLetter: 'Employment letter',
  EnrollmentLetter: 'Enrollment letter',
  Other: 'Other document',
};

function normalizeJobType(value: unknown): string {
  if (value === '') return '';
  if (typeof value !== 'string') return 'Employed';

  if (employmentJobTypes.includes(value as (typeof employmentJobTypes)[number])) {
    return value;
  }

  const upper = value.toUpperCase();
  if (upper === 'EMPLOYED') return 'Employed';
  if (upper === 'SELF_EMPLOYED' || upper === 'SELFEMPLOYED') return 'SelfEmployed';
  if (upper === 'UNEMPLOYED') return 'Unemployed';
  if (upper === 'STUDENT') return 'Student';

  return 'Other';
}

function normalizeEmploymentIncome(value: unknown): number | '' {
  if (value == null || typeof value === 'string' && !value.trim()) return '';
  const income = Number(value);
  return Number.isFinite(income) ? income : '';
}

function normalizeOccupancyType(value: unknown): string {
  if (typeof value !== 'string') return 'Rent';

  if (occupancyTypes.includes(value as (typeof occupancyTypes)[number])) {
    return value;
  }

  const upper = value.toUpperCase();
  if (upper === 'RENT') return 'Rent';
  if (upper === 'OWN') return 'Own';

  return 'Other';
}

function formatAttachmentSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function getAttachmentExtension(fileName: string): string {
  return fileName.split('.').pop()?.toLowerCase() || '';
}

function getAttachmentFileIcon(fileName: string): string {
  const extension = getAttachmentExtension(fileName);
  if (extension === 'pdf') return 'document';
  if (extension === 'jpg' || extension === 'jpeg' || extension === 'png' || extension === 'webp') {
    return 'image';
  }
  return 'document';
}

function normalizeTemplateQuestion(item: ApplicationQuestion): TemplateQuestion {
  const orderIndex = Number(item.orderIndex);
  return {
    ...item,
    type: String(item.type),
    orderIndex: Number.isFinite(orderIndex) ? orderIndex : 0,
  };
}

function normalizeTemplateQuestions(items: ApplicationQuestion[] | undefined): TemplateQuestion[] {
  return (items || [])
    .filter((item) => item.active)
    .map(normalizeTemplateQuestion)
    .sort((a, b) => a.orderIndex - b.orderIndex);
}

interface DraftHydrationState {
  email: string;
  requestedStep: StepKey;
  maxReachedStep: StepKey;
  stepPayloads: Record<string, Record<string, unknown>>;
  stepCompletion: StepCompletionMap;
  attachments: PublicDraftAttachmentResponse[];
  templateQuestions: TemplateQuestion[];
}

function hydrateDraftSnapshot(data: ApplicationDraft | undefined): DraftHydrationState {
  if (!data) {
    return {
      email: '',
      requestedStep: 'move_in',
      maxReachedStep: 'move_in',
      stepPayloads: {},
      stepCompletion: {},
      attachments: [],
      templateQuestions: [],
    };
  }

  const steps = data.steps;
  const stepPayloads: Record<string, Record<string, unknown>> = {};
  const stepCompletion: StepCompletionMap = {};

  steps.forEach((step) => {
    const parsed = parsePayload(step.payloadJson);
    if (parsed && typeof parsed === 'object') {
      stepPayloads[step.stepKey] = parsed as Record<string, unknown>;
    }

    if (isStepKey(step.stepKey)) {
      stepCompletion[step.stepKey] = step.isComplete;
    }
  });

  const loadedCurrentStep = isStepKey(data.currentStep) ? data.currentStep : 'move_in';
  const loadedIsCoSigner = stepPayloads.move_in?.applicant_role === 'COSIGNER';
  const requestedStep = loadedCurrentStep === 'household' && loadedIsCoSigner
    ? 'about_you'
    : loadedCurrentStep;

  const loadedStepKeys = new Set(steps.map((step) => step.stepKey));
  let maxReachedStep: StepKey = 'move_in';
  for (const step of allSteps) {
    if (loadedStepKeys.has(step)) maxReachedStep = step;
  }

  const currentIndex = isStepKey(data.currentStep) ? allSteps.indexOf(data.currentStep) : -1;
  const maxReachedIndex = allSteps.indexOf(maxReachedStep);
  if (currentIndex > maxReachedIndex && isStepKey(data.currentStep)) {
    maxReachedStep = data.currentStep;
  }

  return {
    email: data.email || '',
    requestedStep,
    maxReachedStep,
    stepPayloads,
    stepCompletion,
    attachments: (data.attachments || []).map(withoutFileId),
    templateQuestions: normalizeTemplateQuestions(data.templateQuestions),
  };
}

function normalizeCustomAnswers(payload: Record<string, unknown>): Array<{ question_id: string; answer: CustomAnswerValue }> {
  const rawAnswers = payload.answers;
  if (!Array.isArray(rawAnswers)) return [];

  return rawAnswers
    .map((item) => {
      const row = (item || {}) as Record<string, unknown>;
      const questionId = typeof row.question_id === 'string'
        ? row.question_id
        : typeof row.questionId === 'string'
          ? row.questionId
          : '';
      const answer = (row.answer ?? row.value ?? null) as CustomAnswerValue;
      return { question_id: questionId, answer };
    })
    .filter((item) => item.question_id);
}

function createEmploymentEntry(): EmploymentEntry {
  return {
    job_type: '',
    annual_income: '',
    title: '',
    company_name: '',
    currently_work_here: false,
    start_date: '',
    end_date: '',
    supervisor_name: '',
    supervisor_email: '',
    supervisor_phone: '',
  };
}

function createResidenceEntry(): ResidenceEntry {
  return {
    occupancy_type: 'Rent',
    address_line_1: '',
    city: '',
    state: '',
    move_in_date: '',
    move_out_date: '',
  };
}

function isStepKey(value: string): value is StepKey {
  return allSteps.includes(value as StepKey);
}

export function parsePayload(value: string): unknown {
  try {
    return JSON.parse(value);
  } catch {
    return value;
  }
}

function formatDate(value: string | null | undefined): string {
  if (!value) return '-';
  return new Date(value).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function applyStepDefaults(step: StepKey, payload: Record<string, unknown>): Record<string, unknown> {
  if (step === 'move_in') {
    return {
      applicant_role: 'TENANT',
      ...payload,
    };
  }

  if (step === 'household') {
    return {
      moving_with: 'ALONE',
      ...payload,
    };
  }

  return payload;
}

function makeTouchedKey(step: StepKey, field: string): string {
  return `${step}.${field}`;
}

function getFieldErrorId(step: StepKey, field: string): string {
  return `${step}-${field.replace(/[^a-zA-Z0-9_-]/g, '-')}-error`;
}

function isBlankValue(value: unknown): boolean {
  if (value === null || value === undefined) return true;
  if (typeof value === 'string') return value.trim().length === 0;
  if (typeof value === 'number') return !Number.isFinite(value);
  if (typeof value === 'boolean') return false;
  if (Array.isArray(value)) return value.length === 0 || value.every(isBlankValue);
  return false;
}

function isValidEmail(value: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());
}

function isValidPhone(value: string): boolean {
  const digits = value.replace(/\D/g, '');
  return digits.length >= 10 && digits.length <= 15;
}

function parseLocalDateOnly(value: unknown): Date | null {
  if (typeof value !== 'string' || !value.trim()) return null;
  const match = value.match(/^(\d{4})-(\d{1,2})-(\d{1,2})/);
  if (!match) return null;

  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const date = new Date(year, month - 1, day);

  if (
    date.getFullYear() !== year ||
    date.getMonth() !== month - 1 ||
    date.getDate() !== day
  ) {
    return null;
  }

  return date;
}

function isAtLeastAge(value: unknown, minimumAge: number): boolean {
  const birthDate = parseLocalDateOnly(value);
  if (!birthDate) return false;

  const today = new Date();
  let age = today.getFullYear() - birthDate.getFullYear();
  const hasHadBirthdayThisYear =
    today.getMonth() > birthDate.getMonth() ||
    (today.getMonth() === birthDate.getMonth() && today.getDate() >= birthDate.getDate());

  if (!hasHadBirthdayThisYear) {
    age -= 1;
  }

  return age >= minimumAge;
}

function hasAnyResidenceValue(entry: ResidenceEntry): boolean {
  return Boolean(
    entry.address_line_1.trim() ||
      entry.city.trim() ||
      entry.state.trim() ||
      entry.move_in_date.trim() ||
      entry.move_out_date.trim()
  );
}

function getCustomAnswer(payload: Record<string, unknown>, questionId: string): CustomAnswerValue {
  return normalizeCustomAnswers(payload).find((item) => item.question_id === questionId)?.answer ?? null;
}

function validateStepFields(
  step: StepKey,
  payload: Record<string, unknown>,
  context: StepValidationContext,
): FieldErrors {
  const errors: FieldErrors = {};

  if (step === 'move_in') {
    if (isBlankValue(payload.expected_move_in_date)) {
      errors.expected_move_in_date = 'Expected move-in date is required.';
    }

    if (isBlankValue(payload.applicant_role)) {
      errors.applicant_role = 'Applicant role is required.';
    }
  }

  if (step === 'household') {
    if (isBlankValue(payload.moving_with)) {
      errors.moving_with = 'Household selection is required.';
    }
  }

  if (step === 'about_you') {
    if (isBlankValue(payload.first_name)) {
      errors.first_name = 'First name is required.';
    }

    if (isBlankValue(payload.last_name)) {
      errors.last_name = 'Last name is required.';
    }

    const emailValue = typeof payload.email === 'string' ? payload.email : '';
    if (isBlankValue(emailValue)) {
      errors.email = 'Email is required.';
    } else if (!isValidEmail(emailValue)) {
      errors.email = 'Enter a valid email address.';
    }

    const phoneValue = typeof payload.phone === 'string' ? payload.phone : '';
    if (isBlankValue(phoneValue)) {
      errors.phone = 'Phone number is required.';
    } else if (!isValidPhone(phoneValue)) {
      errors.phone = 'Enter a valid phone number.';
    }

    if (isBlankValue(payload.date_of_birth)) {
      errors.date_of_birth = 'Date of birth is required.';
    } else if (!isAtLeastAge(payload.date_of_birth, 18)) {
      errors.date_of_birth = 'Applicants must be 18 or older.';
    }

  }

  if (step === 'employment') {
    context.employmentEntries.forEach((entry, index) => {
      const prefix = `employment.${index}`;
      const isEmployedType = entry.job_type === 'Employed' || entry.job_type === 'SelfEmployed';

      if (isBlankValue(entry.job_type)) {
        errors[`${prefix}.job_type`] = 'Job type is required.';
        return;
      }

      if (entry.annual_income === '' || !Number.isFinite(Number(entry.annual_income))) {
        errors[`${prefix}.annual_income`] = 'Annual income is required.';
      }

      if (isEmployedType) {
        if (isBlankValue(entry.title)) {
          errors[`${prefix}.title`] = 'Title is required.';
        }

        if (isBlankValue(entry.company_name)) {
          errors[`${prefix}.company_name`] = 'Company name is required.';
        }

        if (isBlankValue(entry.start_date)) {
          errors[`${prefix}.start_date`] = 'Start date is required.';
        }

        if (!entry.currently_work_here && isBlankValue(entry.end_date)) {
          errors[`${prefix}.end_date`] = 'End date is required when this is not your current job.';
        }
      }
    });
  }

  if (step === 'residential_history') {
    context.residenceEntries.forEach((entry, index) => {
      if (!hasAnyResidenceValue(entry)) return;

      const prefix = `residence.${index}`;
      if (isBlankValue(entry.address_line_1)) {
        errors[`${prefix}.address_line_1`] = 'Address is required for added residence history.';
      }
      if (isBlankValue(entry.city)) {
        errors[`${prefix}.city`] = 'City is required for added residence history.';
      }
      if (isBlankValue(entry.state)) {
        errors[`${prefix}.state`] = 'State is required for added residence history.';
      }
      if (isBlankValue(entry.move_in_date)) {
        errors[`${prefix}.move_in_date`] = 'Move-in date is required for added residence history.';
      }
    });
  }

  if (step === 'custom_questions') {
    context.templateQuestions.forEach((question) => {
      if (!question.required) return;

      const answer = getCustomAnswer(payload, question.id);
      if (isBlankValue(answer)) {
        errors[`custom.${question.id}`] = `${question.label} is required.`;
      }
    });
  }

  return errors;
}

export type UnitDetails = PublicRentalApplicationUnitLocationState & ScreeningFacts;

interface PublicRentalApplicationApplyViewProps extends PublicRentalApplicationApplyCoreProps {
  unit: UnitDetails;
  draft: ApplicationDraft;
  legal: ApplicantLegalProjection;
}

function PublicRentalApplicationApplyView({
  organizationSlug,
  unitPublicKey,
  unit,
  draft: draftSnapshot,
  legal,
}: PublicRentalApplicationApplyViewProps) {
  const hydrated = useSyncExternalStore(subscribeHydration, () => true, () => false);
  const { navigate, searchParams } = usePublicRouteBridge();
  const router = useRouter();
  const workspace = useWorkspaceActionsStore();
  const stepFormId = useId();
  // The draft exists before this view mounts: the customer account is the capability, and its id names the draft.
  const draftHandle = draftSnapshot.draftId;

  const initialDraft = hydrateDraftSnapshot(draftSnapshot);

  const email = initialDraft.email;
  const [requestedStep, setRequestedStep] = useState<StepKey>(initialDraft.requestedStep);
  const [maxReachedStep, setMaxReachedStep] = useState<StepKey>(initialDraft.maxReachedStep);
  const [stepDirection, setStepDirection] = useState(1);
  const [stepPayloads, setStepPayloads] = useState<Record<string, Record<string, unknown>>>(() => {
    // `?moveIn=` from the unit page prefills step 1 once. It stays editable and unsaved until Continue.
    const requestedMoveIn = parseIsoDate(searchParams.get('moveIn'));
    if (!requestedMoveIn || initialDraft.stepPayloads.move_in?.expected_move_in_date) return initialDraft.stepPayloads;
    return { ...initialDraft.stepPayloads, move_in: { ...initialDraft.stepPayloads.move_in, expected_move_in_date: toIsoDate(requestedMoveIn) } };
  });
  const [savedStepPayloads, setSavedStepPayloads] = useState(initialDraft.stepPayloads);
  const saveInFlight = useRef(false);
  const requestInFlight = useRef(false);
  const attachmentInFlight = useRef(false);
  const [submissionOutcomeUnknown, setSubmissionOutcomeUnknown] = useState(false);
  /** One idempotency key per draft: a retry after a lost response returns the same application, never a second one. */
  const submitKey = useRef<string>(`submit-${draftHandle}`);
  const [stepCompletion, setStepCompletion] = useState<StepCompletionMap>(initialDraft.stepCompletion);
  const [attachments, setAttachments] = useState<PublicDraftAttachmentResponse[]>(initialDraft.attachments);
  const attachmentInputRef = useRef<HTMLInputElement>(null);
  const [isAttachmentDragging, setIsAttachmentDragging] = useState(false);
  const [isUploadingAttachment, setIsUploadingAttachment] = useState(false);
  const [uploadingAttachmentName, setUploadingAttachmentName] = useState('');
  const [attachmentUploadError, setAttachmentUploadError] = useState<string | null>(null);
  const [templateQuestions] = useState<TemplateQuestion[]>(initialDraft.templateQuestions);
  const [lastSavedAt, setLastSavedAt] = useState<Date | null>(null);
  const [isSavingStep, setIsSavingStep] = useState(false);
  const [stepError, setStepError] = useState<string | null>(null);
  const [touchedFields, setTouchedFields] = useState<TouchedFieldMap>({});
  const [submittedSteps, setSubmittedSteps] = useState<SubmittedStepMap>({});

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [certifiedApplication, setCertifiedApplication] = useState(false);
  const [typedSignature, setTypedSignature] = useState('');
  const [submitResult, setSubmitResult] = useState<SubmittedApplicationReceipt | null>(null);
  const termsDocument = legal.documents.find(document => document.id === applicantDocumentIds.terms);

  // The business's name comes from branding, never from the slug (spec §2).
  const { branding } = usePublicShell();
  const businessName = branding.name;
  const applicationUnit = unit;
  const applicationImages = applicationUnit?.photoUrls ?? [];

  const moveInPayload = applyStepDefaults('move_in', stepPayloads.move_in || {});
  const isCoSigner = moveInPayload.applicant_role === 'COSIGNER';
  const activeTemplateQuestions = useMemo(
    () => templateQuestions.filter((question) => question.active),
    [templateQuestions],
  );

  const visibleSteps = useMemo(() => {
    const steps = activeTemplateQuestions.length === 0
      ? allSteps.filter((step) => step !== 'custom_questions')
      : allSteps;

    if (isCoSigner) {
      return steps.filter((step) => step !== 'household');
    }

    return steps;
  }, [activeTemplateQuestions.length, isCoSigner]);

  // Household is asked on the move-in screen, but stays its own step and payload (spec §5.3).
  const screens = useMemo((): StepKey[] => visibleSteps.filter((step) => step !== 'household'), [visibleSteps]);
  const screenOf = (step: StepKey): StepKey => (step === 'household' ? 'move_in' : step);

  const currentStep = useMemo<StepKey>(() => {
    if (isCoSigner && requestedStep === 'household') {
      return 'about_you';
    }

    if (requestedStep === 'household') {
      return 'move_in';
    }

    if (visibleSteps.includes(requestedStep)) {
      return requestedStep;
    }

    const requestedIndex = allSteps.indexOf(requestedStep);
    return visibleSteps.find((step) => allSteps.indexOf(step) > requestedIndex)
      ?? visibleSteps[visibleSteps.length - 1]
      ?? 'move_in';
  }, [isCoSigner, requestedStep, visibleSteps]);

  const stepIndex = screens.indexOf(currentStep);
  const safeStepIndex = Math.max(stepIndex, 0);
  const maxReachedIndex = Math.max(screens.indexOf(screenOf(maxReachedStep)), 0);
  const isStepCompleted = (step: StepKey) =>
    isRentalApplicationStepCompleted(step, currentStep, stepCompletion);
  const saveStatusLabel = isSavingStep
    ? 'Saving…'
    : lastSavedAt
      ? `Saved ${lastSavedAt.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}`
      : 'Saves when you continue';
  const previousStepLabel = safeStepIndex > 0 ? stepShortNames[screens[safeStepIndex - 1]] : '';
  const showsHousehold = currentStep === 'move_in' && !isCoSigner;

  // About you always carries the verified address (the page shows it read-only), on the saved side too, so it never counts as an edit.
  const currentPayload = useMemo(() => {
    const payload = applyStepDefaults(currentStep, stepPayloads[currentStep] || {});
    return currentStep === 'about_you' ? withVerifiedEmail(payload, email) : payload;
  }, [currentStep, email, stepPayloads]);

  const savedCurrentPayload = applyStepDefaults(currentStep, savedStepPayloads[currentStep] || {});
  const savedPayload = currentStep === 'about_you' ? withVerifiedEmail(savedCurrentPayload, email) : savedCurrentPayload;
  const householdPayload = applyStepDefaults('household', stepPayloads.household || {});
  const savedHouseholdPayload = applyStepDefaults('household', savedStepPayloads.household || {});
  const householdDirty = showsHousehold && JSON.stringify(householdPayload) !== JSON.stringify(savedHouseholdPayload);
  const stepDirty = JSON.stringify(currentPayload) !== JSON.stringify(savedPayload) || householdDirty;
  const reviewDirty = currentStep === 'review' && (certifiedApplication || Boolean(typedSignature));
  const draftDirty = Boolean(draftHandle && !submitResult && (stepDirty || reviewDirty || submissionOutcomeUnknown));
  const draftPending = Boolean(draftHandle && !submitResult && (isSavingStep || isSubmitting || isUploadingAttachment));

  // History is local to editable application steps. Verification, consent,
  // certification, signatures, and uploaded attachment receipts stay separate.
  const historyEligible = currentStep !== 'review' && currentStep !== 'attachments';
  const history = useWorkspaceEditHistory({
    value: historyEligible ? currentPayload : {},
    baseline: historyEligible ? savedPayload : {},
    identity: JSON.stringify([organizationSlug, unitPublicKey, draftHandle, currentStep]),
    pending: draftPending,
    disabled: !draftHandle || !historyEligible || submissionOutcomeUnknown || Boolean(submitResult),
    onRestore: (value) => {
      if (saveInFlight.current || requestInFlight.current || attachmentInFlight.current || submissionOutcomeUnknown) return;
      setStepPayloads(current => ({ ...current, [currentStep]: value }));
      setStepError(null);
    },
  });

  async function handleWizardStepClick(index: number) {
    if (index > maxReachedIndex || index === safeStepIndex || saveInFlight.current || requestInFlight.current || attachmentInFlight.current || isUploadingAttachment || submissionOutcomeUnknown) return;
    const step = screens[index];
    if (!step) return;
    if (reviewDirty) { setStepError('Submit the application or discard your certification before leaving review.'); return; }
    if (index > safeStepIndex && !validateCurrentStepForNavigation()) return;
    if (stepDirty && !await saveCurrentScreen()) return;
    setStepDirection(index < safeStepIndex ? -1 : 1);
    setStepError(null); setRequestedStep(step);
  }

  function handleCloseWizard() {
    if (draftDirty || draftPending || saveInFlight.current || requestInFlight.current || attachmentInFlight.current) { workspace.focusBlocked(); return; }
    navigate(rentalListingPath(organizationSlug));
  }

  const signOutForm = useRef<HTMLFormElement>(null);
  /**
   * The address on the application is the one verified at sign-in, and the API refuses any other at submit. To apply with another address the
   * applicant signs in again: the server ends this session and lands on the sign-in page, which verifies the new address and comes back here.
   * It follows the same guard as "Finish later": an unsaved step has to be saved or discarded first.
   */
  function restartWithAnotherEmail() {
    if (draftDirty || draftPending || saveInFlight.current || requestInFlight.current || attachmentInFlight.current) { workspace.focusBlocked(); return; }
    signOutForm.current?.requestSubmit();
  }

  /** A draft saved with another address in About you (before it was locked) is saved again with the verified one, so submitting cannot fail on it. */
  async function repairVerifiedEmail(): Promise<boolean> {
    const stale = savedStepPayloads.about_you;
    if (!stale || !needsEmailRepair(stale, email)) return true;
    return saveStep('about_you', withVerifiedEmail(stale, email), true);
  }

  function discardCurrentStep() {
    if (draftPending || saveInFlight.current || requestInFlight.current || attachmentInFlight.current || submissionOutcomeUnknown) return;
    setStepPayloads(current => ({
      ...current,
      [currentStep]: savedStepPayloads[currentStep] || {},
      ...(showsHousehold ? { household: savedStepPayloads.household || {} } : {}),
    }));
    history.checkpoint(historyEligible ? savedPayload : {});
    if (currentStep === 'review') { setCertifiedApplication(false); setTypedSignature(''); }
    setStepError(null);
  }

  const employmentEntries = useMemo<EmploymentEntry[]>(() => {
    const fromArray = currentPayload.employments;
    if (Array.isArray(fromArray) && fromArray.length > 0) {
      return fromArray.map((item) => {
        const row = (item || {}) as Record<string, unknown>;
        const annualIncome = normalizeEmploymentIncome(row.annual_income);
        return {
          job_type: normalizeJobType(row.job_type),
          annual_income: annualIncome,
          title: typeof row.title === 'string' ? row.title : '',
          company_name: typeof row.company_name === 'string' ? row.company_name : '',
          currently_work_here: row.currently_work_here === true,
          start_date: typeof row.start_date === 'string' ? row.start_date : '',
          end_date: typeof row.end_date === 'string' ? row.end_date : '',
          supervisor_name: typeof row.supervisor_name === 'string' ? row.supervisor_name : '',
          supervisor_email: typeof row.supervisor_email === 'string' ? row.supervisor_email : '',
          supervisor_phone: typeof row.supervisor_phone === 'string' ? row.supervisor_phone : '',
        };
      });
    }

    const annualIncome = normalizeEmploymentIncome(currentPayload.annual_income);
    const hasLegacyValues =
      typeof currentPayload.job_type === 'string' ||
      typeof currentPayload.title === 'string' ||
      typeof currentPayload.company_name === 'string' ||
      annualIncome !== '';

    if (hasLegacyValues) {
      return [
        {
          job_type: normalizeJobType(currentPayload.job_type),
          annual_income: annualIncome,
          title:
            typeof currentPayload.title === 'string'
              ? currentPayload.title
              : '',
          company_name:
            typeof currentPayload.company_name === 'string'
              ? currentPayload.company_name
              : '',
          currently_work_here: currentPayload.currently_work_here === true,
          start_date:
            typeof currentPayload.start_date === 'string'
              ? currentPayload.start_date
              : '',
          end_date:
            typeof currentPayload.end_date === 'string'
              ? currentPayload.end_date
              : '',
          supervisor_name:
            typeof currentPayload.supervisor_name === 'string'
              ? currentPayload.supervisor_name
              : '',
          supervisor_email:
            typeof currentPayload.supervisor_email === 'string'
              ? currentPayload.supervisor_email
              : '',
          supervisor_phone:
            typeof currentPayload.supervisor_phone === 'string'
              ? currentPayload.supervisor_phone
              : '',
        },
      ];
    }

    return [createEmploymentEntry()];
  }, [currentPayload]);

  const residenceEntries = useMemo<ResidenceEntry[]>(() => {
    const fromArray = currentPayload.residences;
    if (Array.isArray(fromArray) && fromArray.length > 0) {
      return fromArray.map((item) => {
        const row = (item || {}) as Record<string, unknown>;
        return {
          occupancy_type: normalizeOccupancyType(row.occupancy_type),
          address_line_1:
            typeof row.address_line_1 === 'string' ? row.address_line_1 : '',
          city: typeof row.city === 'string' ? row.city : '',
          state: typeof row.state === 'string' ? row.state : '',
          move_in_date:
            typeof row.move_in_date === 'string' ? row.move_in_date : '',
          move_out_date:
            typeof row.move_out_date === 'string' ? row.move_out_date : '',
        };
      });
    }

    const hasLegacyValues =
      typeof currentPayload.address_line_1 === 'string' ||
      typeof currentPayload.city === 'string' ||
      typeof currentPayload.state === 'string' ||
      typeof currentPayload.move_in_date === 'string' ||
      typeof currentPayload.move_out_date === 'string';

    if (hasLegacyValues) {
      return [
        {
          occupancy_type: normalizeOccupancyType(currentPayload.occupancy_type),
          address_line_1:
            typeof currentPayload.address_line_1 === 'string'
              ? currentPayload.address_line_1
              : '',
          city:
            typeof currentPayload.city === 'string' ? currentPayload.city : '',
          state:
            typeof currentPayload.state === 'string'
              ? currentPayload.state
              : '',
          move_in_date:
            typeof currentPayload.move_in_date === 'string'
              ? currentPayload.move_in_date
              : '',
          move_out_date:
            typeof currentPayload.move_out_date === 'string'
              ? currentPayload.move_out_date
              : '',
        },
      ];
    }

    return [];
  }, [currentPayload]);

  function setEmploymentEntries(entries: EmploymentEntry[]) {
    updateStepPayload(employmentPayload(entries, createEmploymentEntry()));
  }

  function updateEmploymentEntry(index: number, patch: Partial<EmploymentEntry>) {
    Object.keys(patch).forEach((field) => markFieldTouched(`employment.${index}.${field}`));

    const next = employmentEntries.map((row, rowIndex) =>
      rowIndex === index ? { ...row, ...patch } : row,
    );
    setEmploymentEntries(next);
  }

  function addEmploymentEntry() {
    setEmploymentEntries([...employmentEntries, createEmploymentEntry()]);
  }

  function removeEmploymentEntry(index: number) {
    const next = employmentEntries.filter((_, rowIndex) => rowIndex !== index);
    setEmploymentEntries(next.length > 0 ? next : [createEmploymentEntry()]);
  }

  function setResidenceEntries(entries: ResidenceEntry[]) {
    const primary = entries[0] || createResidenceEntry();

    updateStepPayload({
      residences: entries,
      occupancy_type: primary.occupancy_type || null,
      address_line_1: primary.address_line_1 || null,
      city: primary.city || null,
      state: primary.state || null,
      move_in_date: primary.move_in_date || null,
      move_out_date: primary.move_out_date || null,
    });
  }

  function updateResidenceEntry(index: number, patch: Partial<ResidenceEntry>) {
    Object.keys(patch).forEach((field) => markFieldTouched(`residence.${index}.${field}`));

    const next = residenceEntries.map((row, rowIndex) =>
      rowIndex === index ? { ...row, ...patch } : row,
    );
    setResidenceEntries(next);
  }

  function addResidenceEntry() {
    setResidenceEntries([...residenceEntries, createResidenceEntry()]);
  }

  function removeResidenceEntry(index: number) {
    setResidenceEntries(residenceEntries.filter((_, rowIndex) => rowIndex !== index));
  }

  function getCustomQuestionAnswer(questionId: string): CustomAnswerValue {
    const answer = normalizeCustomAnswers(currentPayload)
      .find((item) => item.question_id === questionId);
    return answer?.answer ?? null;
  }

  function updateCustomQuestionAnswer(questionId: string, answer: CustomAnswerValue) {
    markFieldTouched(`custom.${questionId}`);

    const answers = normalizeCustomAnswers(currentPayload);
    const existingIndex = answers.findIndex((item) => item.question_id === questionId);
    const nextAnswer = { question_id: questionId, answer };

    const next = existingIndex >= 0
      ? answers.map((item, index) => (index === existingIndex ? nextAnswer : item))
      : [...answers, nextAnswer];

    updateStepPayload({ answers: next });
  }

  function toggleCustomQuestionOption(questionId: string, option: string, checked: boolean) {
    const currentValue = getCustomQuestionAnswer(questionId);
    const currentValues = Array.isArray(currentValue)
      ? currentValue.filter((item): item is string => typeof item === 'string')
      : [];
    const nextValues = checked
      ? [...new Set([...currentValues, option])]
      : currentValues.filter((item) => item !== option);

    updateCustomQuestionAnswer(questionId, nextValues);
  }

  const selectedAttachmentType = (() => {
    const value = currentPayload.attachment_category;
    if (
      typeof value === 'string' &&
      attachmentTypes.includes(value as (typeof attachmentTypes)[number])
    ) {
      return value;
    }
    return 'Other';
  })();
  const currentStepValidationErrors = useMemo(
    () => {
      const context = { employmentEntries, residenceEntries, templateQuestions: activeTemplateQuestions };
      const errors = validateStepFields(currentStep, currentPayload, context);
      return showsHousehold ? { ...errors, ...validateStepFields('household', householdPayload, context) } : errors;
    },
    [
      activeTemplateQuestions,
      currentPayload,
      currentStep,
      employmentEntries,
      householdPayload,
      residenceEntries,
      showsHousehold,
    ],
  );

  function markFieldTouched(field: string, step: StepKey = currentStep) {
    const key = makeTouchedKey(step, field);
    setTouchedFields((prev) => (prev[key] ? prev : { ...prev, [key]: true }));
  }

  function markCurrentStepSubmitted(step: StepKey = currentStep) {
    setSubmittedSteps((prev) => (prev[step] ? prev : { ...prev, [step]: true }));
  }

  function getVisibleFieldError(field: string): string | null {
    const shouldShow =
      touchedFields[makeTouchedKey(currentStep, field)] === true ||
      submittedSteps[currentStep] === true;

    return shouldShow ? currentStepValidationErrors[field] ?? null : null;
  }

  function getFieldA11y(field: string) {
    const error = getVisibleFieldError(field);
    const errorId = getFieldErrorId(currentStep, field);

    return {
      error,
      errorId,
      invalid: Boolean(error),
      describedBy: error ? errorId : undefined,
    };
  }


  function validateCurrentStepForNavigation(): boolean {
    if (Object.keys(currentStepValidationErrors).length === 0) {
      return true;
    }

    markCurrentStepSubmitted();
    setStepError('Please complete the required fields before continuing.');
    return false;
  }

  async function saveStep(stepKey: StepKey, payload: Record<string, unknown>, silent = false) {
    if (!draftHandle || saveInFlight.current || requestInFlight.current || attachmentInFlight.current || isUploadingAttachment) return false;
    saveInFlight.current = true;
    const showSavingState = true;

    if (showSavingState) {
      setIsSavingStep(true);
    }

    if (!silent) {
      setStepError(null);
    }

    try {
      const result = await applyApi.saveStep(draftHandle, stepKey, payload);

      if (result.errors?.length) {
        setStepCompletion((prev) => ({ ...prev, [stepKey]: false }));
        const firstMessage = result.errors[0]?.message || 'Please check this step fields';
        if (!silent) {
          setStepError(firstMessage);
          customToast.error('Step validation failed', firstMessage);
        }
        return false;
      }

      // The move-in screen also saves `household`; only the screen's own step moves the history baseline.
      if (stepKey === currentStep) history.checkpoint(historyEligible ? payload : {});
      setSavedStepPayloads(previous => ({ ...previous, [stepKey]: payload }));
      if (stepKey !== 'review') {
        setStepPayloads((prev) => ({
          ...prev,
          [stepKey]: payload,
        }));
      }
      setStepCompletion((prev) => ({
        ...prev,
        [stepKey]: result.isComplete === true,
      }));
      setLastSavedAt(new Date());
      return true;
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to save step';
      if (!silent) {
        setStepError(message);
        customToast.error('Failed to save step', message);
      }
      return false;
    } finally {
      saveInFlight.current = false;
      if (showSavingState) {
        setIsSavingStep(false);
      }
    }
  }

  /** Saves the screen: its own step, then `household` when it is asked on the move-in screen. */
  async function saveCurrentScreen(): Promise<boolean> {
    if (!await saveStep(currentStep, currentPayload)) return false;
    return showsHousehold ? saveStep('household', householdPayload) : true;
  }

  function updateHouseholdPayload(patch: Record<string, unknown>) {
    if (saveInFlight.current || requestInFlight.current || attachmentInFlight.current || isUploadingAttachment || submissionOutcomeUnknown) return;
    markFieldTouched('moving_with');
    if (stepError) setStepError(null);
    setStepPayloads((prev) => ({ ...prev, household: { ...(prev.household || {}), ...patch } }));
  }

  function updateStepPayload(patch: Record<string, unknown>, touchedField?: string) {
    if (saveInFlight.current || requestInFlight.current || attachmentInFlight.current || isUploadingAttachment || submissionOutcomeUnknown) return;
    if (touchedField) {
      markFieldTouched(touchedField);
    }

    if (stepError) setStepError(null);
    setStepPayloads((prev) => ({
      ...prev,
      [currentStep]: {
        ...(prev[currentStep] || {}),
        ...patch,
      },
    }));
  }

  async function moveStep(direction: 'next' | 'back') {
    if (!isStepKey(currentStep)) return;

    if (saveInFlight.current || requestInFlight.current || attachmentInFlight.current || isUploadingAttachment || submissionOutcomeUnknown) return;
    if (direction === 'back') {
      if (reviewDirty) { setStepError('Submit the application or discard your certification before leaving review.'); return; }
      if (stepDirty && !await saveCurrentScreen()) return;
      const index = screens.indexOf(currentStep);
      const prevStep = screens[index - 1];
      if (prevStep) { setStepDirection(-1); setStepError(null); setRequestedStep(prevStep); }
      return;
    }

    if (!validateCurrentStepForNavigation()) {
      return;
    }

    const saved = await saveCurrentScreen();
    if (!saved) return;

    const index = screens.indexOf(currentStep);
    const nextStep = screens[index + 1];
    if (nextStep) {
      setStepDirection(1);
      setStepError(null);
      setRequestedStep(nextStep);
      const nextIdx = screens.indexOf(nextStep);
      if (nextIdx > maxReachedIndex) setMaxReachedStep(nextStep);
    }
  }

  async function handleUploadAttachment(file: File) {
    if (!draftHandle || attachmentInFlight.current || saveInFlight.current || requestInFlight.current) return;

    const extension = getAttachmentExtension(file.name);
    if (!attachmentExtensions.has(extension)) {
      const message = 'Choose a PDF, DOCX, JPEG, PNG, or WebP file.';
      setAttachmentUploadError(message);
      customToast.error('Unsupported file type', message);
      return;
    }

    if (file.size <= 0) {
      const message = 'This file is empty. Choose another document.';
      setAttachmentUploadError(message);
      customToast.error('Upload failed', message);
      return;
    }

    if (file.size > maxAttachmentSizeBytes) {
      const message = `Files must be smaller than ${formatAttachmentSize(maxAttachmentSizeBytes)}.`;
      setAttachmentUploadError(message);
      customToast.error('File is too large', message);
      return;
    }

    const attachmentType = selectedAttachmentType;
    setAttachmentUploadError(null);
    attachmentInFlight.current = true;
    setIsUploadingAttachment(true);
    setUploadingAttachmentName(file.name);

    try {
      const completed = await applyApi.uploadAttachment(draftHandle, attachmentType, file);

      setAttachments((prev) => [...prev, withoutFileId(completed)]);
      setLastSavedAt(new Date());
      customToast.success('Attachment uploaded');
    } catch (error) {
      if (error instanceof ApplyRequestError && error.sessionEnded) {
        router.replace(`/auth/start?return=${encodeURIComponent(`/apply/${unitPublicKey}`)}`);
        return;
      }
      const message = error instanceof Error ? error.message : 'Unknown error';
      setAttachmentUploadError(message);
      customToast.error('Upload failed', message);
    } finally {
      attachmentInFlight.current = false;
      setIsUploadingAttachment(false);
      setUploadingAttachmentName('');
      if (attachmentInputRef.current) {
        attachmentInputRef.current.value = '';
      }
    }
  }

  function handleAttachmentInputChange(event: ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    if (file) void handleUploadAttachment(file);
  }

  function handleAttachmentDrop(event: DragEvent<HTMLButtonElement>) {
    event.preventDefault();
    setIsAttachmentDragging(false);

    if (isUploadingAttachment) return;

    const file = event.dataTransfer.files?.[0];
    if (file) void handleUploadAttachment(file);
  }

  async function handleSubmitApplication(retry = false) {
    if (!hydrated || !draftHandle || !retry && !legal.legalReady || requestInFlight.current || saveInFlight.current || attachmentInFlight.current || isUploadingAttachment) return;
    if (!retry && (!certifiedApplication || !typedSignature.trim())) {
      customToast.error('Certification and typed signature are required');
      return;
    }
    if (!retry && !await repairVerifiedEmail()) {
      customToast.error('Submission failed', 'Your contact details could not be saved. Try again.');
      return;
    }

    requestInFlight.current = true;
    setIsSubmitting(true);
    setSubmissionOutcomeUnknown(true);
    try {
      // A retry is the same request with the same idempotency key: the API answers with the application it already created.
      const receipt = await applyApi.submit(draftHandle, {
        certifiedApplication,
        typedSignature: typedSignature.trim(),
        rentalApplicationTermsDocumentId: termsDocument!.id,
        rentalApplicationTermsVersion: termsDocument!.version,
        rentalApplicationTermsContentSha256: termsDocument!.contentSha256,
        certificationTextSha256: legal.certification!.textSha256,
      }, submitKey.current);
      if (!isSubmittedApplicationReceipt(receipt)) throw new Error('Submission receipt could not be confirmed.');

      history.checkpoint();
      setSubmissionOutcomeUnknown(false);
      setSubmitResult(receipt);
      customToast.success('Application submitted');
      navigate(`/apply/status/${encodeURIComponent(receipt.applicationId)}`);
    } catch (error) {
      if (error instanceof ApplyRequestError && error.sessionEnded) {
        router.replace(`/auth/start?return=${encodeURIComponent(`/apply/${unitPublicKey}`)}`);
        return;
      }
      // Only a definite answer from the API unlocks the form again; a lost response keeps the saved submission for a retry.
      if (error instanceof ApplyRequestError && error.status >= 400 && error.status < 500 && error.status !== 429 && error.status !== 409) setSubmissionOutcomeUnknown(false);
      customToast.error('Submission failed', error instanceof Error ? error.message : 'Unknown error');
    } finally {
      requestInFlight.current = false;
      setIsSubmitting(false);
    }
  }

  // ---------- presentation (spec §5): logic above is unchanged ----------

  const [openItem, setOpenItem] = useState<{ step: StepKey; index: number | null; isNew: boolean } | null>(null);
  const [supervisorShown, setSupervisorShown] = useState<ReadonlySet<number>>(() => new Set());
  const today = startOfDay(new Date());

  const unitDisplayTitle = unitTitle({ unitName: applicationUnit?.unitName, propertyName: applicationUnit?.propertyName });
  const unitLabel = applicationUnit?.unitName?.trim() || unitDisplayTitle;
  const availableFrom = parseIsoDate(applicationUnit?.availableFrom);
  const listingLink = <PublicRouteLink to={rentalListingPath(organizationSlug)} className={cn(shell.btn, shell.btnTonal)}>See all homes</PublicRouteLink>;

  /** Indexes of jobs or homes that still fail validation, by their error keys. */
  const invalidIndexes = (prefix: 'employment' | 'residence') => new Set(
    Object.keys(currentStepValidationErrors)
      .filter((key) => key.startsWith(`${prefix}.`))
      .map((key) => Number(key.split('.')[1])),
  );
  const invalidJobs = currentStep === 'employment' ? invalidIndexes('employment') : new Set<number>();
  const invalidHomes = currentStep === 'residential_history' ? invalidIndexes('residence') : new Set<number>();
  const pinned = openItem?.step === currentStep ? openItem : null;
  const firstInvalid = (indexes: Set<number>) => (indexes.size > 0 ? Math.min(...indexes) : null);
  const openJob = currentStep !== 'employment' ? null : pinned ? pinned.index : firstInvalid(invalidJobs);
  const openHome = currentStep !== 'residential_history' ? null : pinned ? pinned.index : firstInvalid(invalidHomes);
  const openIsNew = Boolean(pinned?.isNew);

  /** Closes the open job or home once its own fields pass; otherwise shows what is missing. */
  function saveOpenItem(prefix: 'employment' | 'residence', index: number) {
    if (prefix === 'residence' && openIsNew && residenceEntries[index] && !hasAnyResidenceValue(residenceEntries[index])) {
      removeResidenceEntry(index);
      setOpenItem({ step: currentStep, index: null, isNew: false });
      return;
    }
    const missing = Object.keys(currentStepValidationErrors).filter((key) => key.startsWith(`${prefix}.${index}.`));
    if (missing.length > 0) {
      missing.forEach((key) => markFieldTouched(key));
      return;
    }
    setOpenItem({ step: currentStep, index: null, isNew: false });
  }

  function continueFromStep() {
    // A job or home with missing details opens, so its errors are visible.
    const invalid = currentStep === 'employment' ? firstInvalid(invalidJobs) : currentStep === 'residential_history' ? firstInvalid(invalidHomes) : null;
    if (invalid !== null) setOpenItem({ step: currentStep, index: invalid, isNew: false });
    void moveStep('next');
  }

  const fieldA11y = (field: string): FieldA11y => getFieldA11y(field);

  if (submissionOutcomeUnknown && !submitResult) {
    return (
      <TaskScreen
        title="Confirm your submission"
        actions={<button type="button" className={cn(shell.btn, shell.btnBrand)} disabled={!hydrated || isSubmitting} onClick={() => void handleSubmitApplication(true)}>{isSubmitting ? 'Checking…' : 'Retry the saved submission'}</button>}
      >
        <WorkspaceDraft dirty={draftDirty} pending={draftPending} />
        <p>The submission receipt could not be confirmed. Your original certification and request are saved securely. Check them using the same request before making changes.</p>
      </TaskScreen>
    );
  }
  if (applicationUnit.applyEligible === false && !submitResult) {
    return (
      <TaskScreen title="Applications are closed for this unit." actions={listingLink}>
        <WorkspaceDraft dirty={draftDirty} pending={draftPending} />
        <p>{[applicationUnit.propertyName, applicationUnit.unitName].filter(Boolean).join(' · ')}</p>
      </TaskScreen>
    );
  }

  if (submitResult) {
    return (
      <TaskScreen title="Application sent" actions={listingLink}>
        <p>Your rental application has been received. {businessName ? `${businessName} will` : 'They will'} be in touch.</p>
        <p className={applyStyles.receipt}><span>Stage</span>{submitResult.stage}</p>
      </TaskScreen>
    );
  }

  const aboutYou = stepPayloads.about_you;
  const applicantName = [aboutYou?.first_name, aboutYou?.last_name].filter((part): part is string => typeof part === 'string' && Boolean(part.trim())).join(' ') || email;
  const cardLines = applicationCardLines({
    visibleSteps,
    currentStep,
    completed: isStepCompleted,
    payloads: { ...stepPayloads, move_in: moveInPayload },
    isCoSigner,
    documents: attachments.length,
    email,
    today,
  });
  const reachable = (step: StepKey) => screens.indexOf(screenOf(step)) <= maxReachedIndex;
  const isReview = currentStep === 'review';
  const question = currentStep === 'custom_questions' && businessName ? `A few questions from ${businessName}` : stepQuestions[currentStep];
  const lead = stepLeads[currentStep];
  const primary = currentStep === 'employment' && openJob !== null
    ? { label: 'Save job', disabled: draftPending, run: () => saveOpenItem('employment', openJob) }
    : currentStep === 'residential_history' && openHome !== null
      ? { label: 'Save home', disabled: draftPending, run: () => saveOpenItem('residence', openHome) }
      : isReview
        ? { label: isSubmitting ? 'Sending…' : 'Send application', disabled: draftPending || !certifiedApplication || !typedSignature.trim(), run: () => void handleSubmitApplication() }
        : { label: isSavingStep ? 'Saving…' : 'Continue', disabled: draftPending, run: continueFromStep };

  const stepBody = (() => {
    switch (currentStep) {
      case 'move_in':
      case 'household':
        return (
          <MoveInStep
            moveIn={(currentPayload.expected_move_in_date as string) || ''}
            minDate={availableFrom ? toIsoDate(availableFrom) : undefined}
            availability={availableFrom && availableFrom > today ? `Ready ${formatShortDate(availableFrom, today)}` : null}
            role={(currentPayload.applicant_role as string) || 'TENANT'}
            household={(householdPayload.moving_with as string) || 'ALONE'}
            showHousehold={!isCoSigner}
            a11y={fieldA11y}
            onMoveIn={(value) => updateStepPayload({ expected_move_in_date: value }, 'expected_move_in_date')}
            onBlurMoveIn={() => markFieldTouched('expected_move_in_date')}
            onRole={(value) => updateStepPayload({ applicant_role: value }, 'applicant_role')}
            onHousehold={(value) => updateHouseholdPayload({ moving_with: value })}
          />
        );
      case 'about_you': {
        const adult = new Date(today.getFullYear() - 18, today.getMonth(), today.getDate());
        return (
          <AboutYouStep
            values={{
              first_name: (currentPayload.first_name as string) || '',
              last_name: (currentPayload.last_name as string) || '',
              email,
              phone: (currentPayload.phone as string) || '',
              date_of_birth: (currentPayload.date_of_birth as string) || '',
            }}
            a11y={fieldA11y}
            maxBirthDate={toIsoDate(adult)}
            onChange={(field, value) => updateStepPayload({ [field]: value }, field)}
            onTouch={(field) => markFieldTouched(field)}
            onUseDifferentEmail={restartWithAnotherEmail}
          />
        );
      }
      case 'employment':
        return (
          <EmploymentStep
            jobs={employmentEntries}
            openIndex={openJob}
            isNew={openIsNew}
            invalidIndexes={invalidJobs}
            showSupervisor={openJob !== null && (supervisorShown.has(openJob) || Boolean(employmentEntries[openJob]?.supervisor_name || employmentEntries[openJob]?.supervisor_email || employmentEntries[openJob]?.supervisor_phone))}
            a11y={fieldA11y}
            onUpdate={(index, patch) => {
              if (!pinned) setOpenItem({ step: currentStep, index, isNew: false });
              updateEmploymentEntry(index, patch);
            }}
            onTouch={(field) => markFieldTouched(field)}
            onOpen={(index) => setOpenItem({ step: currentStep, index, isNew: false })}
            onAdd={() => {
              addEmploymentEntry();
              setOpenItem({ step: currentStep, index: employmentEntries.length, isNew: true });
            }}
            onCancel={() => {
              if (openJob !== null) removeEmploymentEntry(openJob);
              setSupervisorShown(new Set());
              setOpenItem({ step: currentStep, index: null, isNew: false });
            }}
            onRemove={(index) => {
              removeEmploymentEntry(index);
              setSupervisorShown(new Set());
              setOpenItem({ step: currentStep, index: null, isNew: false });
            }}
            onShowSupervisor={() => { if (openJob !== null) setSupervisorShown((current) => new Set(current).add(openJob)); }}
            onHideSupervisor={() => {
              if (openJob === null) return;
              // Closing the reference clears what was typed in it, so a removed reference leaves nothing behind in what is sent.
              updateEmploymentEntry(openJob, { supervisor_name: '', supervisor_email: '', supervisor_phone: '' });
              setSupervisorShown((current) => { const next = new Set(current); next.delete(openJob); return next; });
            }}
          />
        );
      case 'residential_history':
        return (
          <ResidencesStep
            homes={residenceEntries}
            openIndex={openHome}
            isNew={openIsNew}
            invalidIndexes={invalidHomes}
            empty={<ApplyEmpty title="No homes added yet" />}
            a11y={fieldA11y}
            onUpdate={(index, patch) => {
              if (!pinned) setOpenItem({ step: currentStep, index, isNew: false });
              updateResidenceEntry(index, patch);
            }}
            onTouch={(field) => markFieldTouched(field)}
            onOpen={(index) => setOpenItem({ step: currentStep, index, isNew: false })}
            onAdd={() => {
              addResidenceEntry();
              setOpenItem({ step: currentStep, index: residenceEntries.length, isNew: true });
            }}
            onCancel={() => {
              if (openHome !== null) removeResidenceEntry(openHome);
              setOpenItem({ step: currentStep, index: null, isNew: false });
            }}
            onRemove={(index) => {
              removeResidenceEntry(index);
              setOpenItem({ step: currentStep, index: null, isNew: false });
            }}
          />
        );
      case 'custom_questions':
        return activeTemplateQuestions.length === 0 ? (
          <div className={applyStyles.fields}><ApplyEmpty title="This home has no more questions" /></div>
        ) : (
          <div className={applyStyles.fields}>
            {activeTemplateQuestions.map((item) => (
              <CustomQuestion
                key={item.id}
                question={{ id: item.id, label: item.label, type: item.type, required: Boolean(item.required), options: (item.options ?? []).filter((option): option is string => typeof option === 'string') }}
                value={getCustomQuestionAnswer(item.id)}
                a11y={fieldA11y(`custom.${item.id}`)}
                onAnswer={(answer) => updateCustomQuestionAnswer(item.id, answer)}
                onToggle={(option, checked) => toggleCustomQuestionOption(item.id, option, checked)}
                onTouch={() => markFieldTouched(`custom.${item.id}`)}
              />
            ))}
          </div>
        );
      case 'attachments':
        return (
          <DocumentsStep
            category={selectedAttachmentType}
            categories={attachmentTypes.map((type) => [type, attachmentTypeLabels[type]] as const)}
            onCategory={(value) => updateStepPayload({ attachment_category: value })}
            uploading={isUploadingAttachment}
            uploadingName={uploadingAttachmentName}
            dragging={isAttachmentDragging}
            error={attachmentUploadError}
            documents={attachments.map((attachment) => ({
              id: attachment.attachmentId,
              name: attachment.fileName,
              type: attachmentTypeLabels[attachment.attachmentType as keyof typeof attachmentTypeLabels] || 'Other document',
              uploaded: formatDate(attachment.uploadedAtUtc),
              icon: getAttachmentFileIcon(attachment.fileName),
            }))}
            empty={<ApplyEmpty title="No documents added yet" />}
            inputRef={attachmentInputRef}
            accept={attachmentAccept}
            onInputChange={handleAttachmentInputChange}
            onDragState={setIsAttachmentDragging}
            onDrop={handleAttachmentDrop}
          />
        );
      case 'review':
        return (
          <SignStep
            certified={certifiedApplication}
            onCertified={setCertifiedApplication}
            certificationText={legal.certification?.text ?? ''}
            termsUrl={termsDocument!.url}
            signature={typedSignature}
            onSignature={setTypedSignature}
            disabled={isSubmitting || submissionOutcomeUnknown}
            screeningNotice={screeningNotice(applicationUnit, businessName)}
          />
        );
    }
  })();

  return (
    <>
      <ApplyFrame
        kind={isReview ? 'sign' : 'step'}
        actions={<WorkspaceActionOutlet className={applyStyles.actionOutlet} label="Application step actions" placement="bottom" />}
        topTrailing={(
          <span className={applyStyles.topNote}>
            <span className={cn(applyStyles.saveState, 'num')} aria-live="polite"><UnitfieldIcon name="cloud-check" size={16} aria-hidden="true" />{saveStatusLabel}</span>
            <button type="button" className={applyStyles.textLink} onClick={handleCloseWizard}>Finish later</button>
          </span>
        )}
        panel={(
          <PhotoPanel photos={applicationImages} index={safeStepIndex}>
            <ApplicationCard
              businessName={businessName}
              applicantName={applicantName}
              unitLabel={unitLabel}
              step={safeStepIndex + 1}
              steps={screens.length}
              ready={isReview}
              lines={cardLines}
              review={isReview}
              canEdit={(line) => reachable(line.step)}
              onEdit={(line) => void handleWizardStepClick(screens.indexOf(screenOf(line.step)))}
            />
          </PhotoPanel>
        )}
      >
        <form ref={signOutForm} method="post" action={`/sign-out?apply=${encodeURIComponent(`/apply/${unitPublicKey}`)}`} hidden aria-hidden="true" />
        <form id={stepFormId} onSubmit={(event) => event.preventDefault()} className={applyStyles.screen}>
          <div key={currentStep} className={applyStyles.stepBody} data-direction={stepDirection > 0 ? 'forward' : 'back'}>
            <h1 className={applyStyles.question}>{question}</h1>
            {lead ? <p className={applyStyles.lead}>{lead}</p> : null}
            {stepBody}
            {stepError ? <p className={cn(applyStyles.stepError, applyStyles.stepErrorLine)} role="alert">{stepError}</p> : null}
          </div>
        </form>
      </ApplyFrame>

      <WorkspaceActions visible formId={stepFormId} dirty={draftDirty} pending={draftPending} history={historyEligible ? history : undefined} label="Application step actions">
        <div className={applyStyles.actionRow}>
          {draftDirty && <button type="button" className={cn(shell.btn, shell.btnTonal)} disabled={draftPending || submissionOutcomeUnknown} onClick={discardCurrentStep}>Discard</button>}
          {safeStepIndex > 0 ? (
            <button type="button" className={cn(shell.btn, shell.btnTonal, applyStyles.backButton)} aria-label={`Back to ${previousStepLabel}`} disabled={draftPending || submissionOutcomeUnknown} onClick={() => void moveStep('back')}>
              <UnitfieldIcon name="chevron-left" size={18} aria-hidden="true" />
            </button>
          ) : null}
          <button type="button" className={cn(shell.btn, shell.btnBrand, applyStyles.primaryButton)} disabled={primary.disabled} onClick={primary.run}>
            {primary.label}
          </button>
        </div>
      </WorkspaceActions>
    </>
  );
}

interface ApplyFlowProps extends PublicRentalApplicationApplyCoreProps {
  unit: UnitDetails;
  legal: ApplicantLegalProjection;
}

/**
 * The apply flow of a signed-in customer (spec 4.5). The customer account is the only capability: starting creates (or
 * resumes) the draft for this unit and records the two acknowledgements; everything after that is the same journey as before.
 */
function ApplyFlow(props: ApplyFlowProps) {
  const router = useRouter();
  const { legal, unit, organizationSlug, unitPublicKey } = props;
  const [draft, setDraft] = useState<ApplicationDraft | null>(null);
  const [acknowledgedPrivacy, setAcknowledgedPrivacy] = useState(false);
  const [consentedRecords, setConsentedRecords] = useState(false);
  const [starting, setStarting] = useState(false);
  const [checking, setChecking] = useState(false);
  const { branding } = usePublicShell();
  const startInFlight = useRef(false);

  const privacyDocument = legal.documents.find((document) => document.id === applicantDocumentIds.privacy);
  const electronicDocument = legal.documents.find((document) => document.id === applicantDocumentIds.electronicRecords);
  const listingHref = rentalListingPath(organizationSlug);

  async function start() {
    if (startInFlight.current || !privacyDocument || !electronicDocument) return;
    startInFlight.current = true;
    setStarting(true);
    try {
      const created = await applyApi.createDraft(unitPublicKey);
      if (!created.consent.accepted) {
        await applyApi.recordConsent(created.draftId, {
          acknowledgedApplicantPrivacy: acknowledgedPrivacy,
          consentedToElectronicRecords: consentedRecords,
          applicantPrivacyDocumentId: privacyDocument.id,
          applicantPrivacyVersion: privacyDocument.version,
          applicantPrivacyContentSha256: privacyDocument.contentSha256,
          electronicRecordsDocumentId: electronicDocument.id,
          electronicRecordsVersion: electronicDocument.version,
          electronicRecordsContentSha256: electronicDocument.contentSha256,
        });
      }
      setDraft(created);
      customToast.success(created.isResumed ? 'Draft resumed' : 'Application started');
    } catch (error) {
      if (error instanceof ApplyRequestError && error.sessionEnded) {
        router.replace(`/auth/start?return=${encodeURIComponent(`/apply/${unitPublicKey}`)}`);
        return;
      }
      customToast.error('Unable to start the application', error instanceof Error ? error.message : 'Please try again.');
    } finally {
      startInFlight.current = false;
      setStarting(false);
    }
  }

  if (!legal.legalReady) {
    return (
      <TaskScreen
        title="Applications are temporarily unavailable"
        actions={<>
          <button type="button" className={cn(shell.btn, shell.btnBrand)} disabled={checking} aria-busy={checking || undefined} onClick={() => { setChecking(true); router.refresh(); window.setTimeout(() => setChecking(false), 1500); }}>{checking ? 'Checking…' : 'Check availability again'}</button>
          <PublicRouteLink to={listingHref} className={cn(shell.btn, shell.btnTonal)}>Back to listing</PublicRouteLink>
        </>}
      >
        <p>The current applicant documents could not be loaded. Applications can resume when these documents are available. An existing application’s status stays available from your account.</p>
      </TaskScreen>
    );
  }

  if (draft) {
    return (
      <WorkspaceSurface identity={`rental-application:${organizationSlug}:${unitPublicKey}`} className="public-apply-surface" pageActions="none">
        <PublicRentalApplicationApplyView key={draft.draftId} organizationSlug={organizationSlug} unitPublicKey={unitPublicKey} unit={unit} draft={draft} legal={legal} />
      </WorkspaceSurface>
    );
  }

  const today = startOfDay(new Date());
  const moveIn = parseIsoDate(typeof window === 'undefined' ? null : new URLSearchParams(window.location.search).get('moveIn'));
  const available = parseIsoDate(unit.availableFrom);
  const fit = moveIn
    ? describeMoveInFit({ availableFrom: unit.availableFrom, applyEligible: unit.applyEligible }, moveIn, today)
    : available && available > today ? { tone: 'later' as const, text: `Available ${formatShortDate(available, today)}` } : { tone: 'ok' as const, text: 'Available now' };
  const title = unitTitle({ unitName: unit.unitName, propertyName: unit.propertyName });
  const address = [unit.addressLine1, unit.addressLine2, unit.city, unit.state, unit.postalCode].filter(Boolean).join(', ');
  void branding;
  return (
    <ApplyFrame
      kind="start"
      topTrailing={null}
      panel={(
        <PhotoPanel photos={unit.photoUrls ?? []} index={0}>
          <HomeCard title={title} address={address || null} rent={formatRent(unit.rent)} moveIn={moveIn ? formatShortDate(moveIn, today) : null} fit={fit} />
        </PhotoPanel>
      )}
    >
      {unit.applyEligible === false ? (
        <TaskScreen title="Applications are closed for this unit." actions={<PublicRouteLink to={listingHref} className={cn(shell.btn, shell.btnTonal)}>See all homes</PublicRouteLink>}>
          <p>{[unit.propertyName, unit.unitName].filter(Boolean).join(' · ')}</p>
        </TaskScreen>
      ) : (
        <StartScreen
          unitLabel={unit.unitName?.trim() || title}
          privacy={{ checked: acknowledgedPrivacy, url: privacyDocument!.url, onChange: setAcknowledgedPrivacy }}
          electronic={{ checked: consentedRecords, url: electronicDocument!.url, onChange: setConsentedRecords }}
          disabled={starting}
          starting={starting}
          onSubmit={() => void start()}
        />
      )}
    </ApplyFrame>
  );
}

export interface PublicRentalApplicationApplyProps extends PublicRentalApplicationApplyCoreProps {
  unit: UnitDetails;
}

/** Loads the published applicant documents through the BFF, then hands over to the flow. Tokens never reach a Server Component. */
export function PublicRentalApplicationApply(props: PublicRentalApplicationApplyProps) {
  const router = useRouter();
  const legalQuery = useQuery<ApplicantLegalProjection, Error>({ queryKey: ['apply-legal', props.organizationSlug], queryFn: () => applyApi.legal(), retry: false, staleTime: 0, gcTime: 0 });
  const sessionEnded = legalQuery.error instanceof ApplyRequestError && legalQuery.error.sessionEnded;
  const returnHere = `/apply/${props.unitPublicKey}`;

  useEffect(() => {
    if (sessionEnded) router.replace(`/auth/start?return=${encodeURIComponent(returnHere)}`);
  }, [sessionEnded, router, returnHere]);

  if (legalQuery.isPending || sessionEnded) return <PublicLoading label="Loading application" />;
  if (legalQuery.isError) {
    return (
      <TaskScreen
        title="Applications are temporarily unavailable"
        actions={<button type="button" className={cn(shell.btn, shell.btnBrand)} disabled={legalQuery.isFetching} onClick={() => void legalQuery.refetch()}>Check availability again</button>}
      >
        <p>The application could not be loaded. Please try again in a moment.</p>
      </TaskScreen>
    );
  }
  return <ApplyFlow {...props} legal={legalQuery.data} />;
}
